import { SingleIndex, Type } from '@deqo/foundation';
import { injectable } from 'inversify';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../../concepts/objects';
import { ClipData } from '../../../platform/Clipboard';
import { Point, PointSource } from '../../../platform/geom/Point';
import { Activator, Connector, resolve } from '../../Container';
import { CsvDataProcessor } from '../../packages/tabularImport/CsvDataProcessor';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { GridSystem } from '../GridSystem';
import { MyRoleInfo } from '../MyRoleInfo';
import { Selection, SelectionSet } from '../Selection';
import { Physical } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { Root } from '../entities/Root';
import { ContentPayload } from './ContentPayload';
import { ContentProcessor } from './ContentProcessor';
import { DeqoData } from './DeqoData';
import { DeqoStyleDataProcessor } from './DeqoStyleDataProcessor';
import { JsonDataProcessor } from './JsonDataProcessor';
import { SerializedDeqoDataProcessor } from './SerializedDeqoDataProcessor';
import { TextDataProcessor } from './TextDataProcessor';


const typeSorter = (a: string, b: string) => {
    if (a !== b) {
        if (a.startsWith('x/deqo')) return -1;
        if (b.startsWith('x/deqo')) return 1;
        if (a === 'text/plain') return -1;
        if (b === 'text/plain') return -1;
    }
    return 0;
};

const processorSorter = (a: ContentProcessor, b: ContentProcessor) => {
    const ap = a.priority ?? 5;
    const bp = b.priority ?? 5;
    return ap - bp;
};

const cascadedUnlock = (p: Physical) => {
    p.setMaybe('locked', false);
    p.polarize(Physical).forEach(x => cascadedUnlock(x));
};

export interface InjectNativeOptions {
    position?: PointSource | 'auto' | false;
    space?: number;
    focus?: 'animate' | 'instant';
    place?: boolean;
    useRefAsId?: boolean;
}

@injectable()
export class ContentInjector {

    @resolve(Activator) private activate!: Activator;
    @resolve(Camera) private camera!: Camera;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(GridSystem) private grid!: GridSystem;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(EditorSession) private session!: EditorSession;

    private processors = new SingleIndex<string, ContentProcessor>();

    public extend(cpt: Type<ContentProcessor>) {
        const cp = this.activate.call(cpt);
        for (const st of cp.supportedTypes) {
            this.processors.add(st, cp);
        }
    }

    public injectNative(ddt: any[], options: InjectNativeOptions = {}): void {
        if (!this.canEdit()) return;
        const { dispatch, objects, session } = this;

        // Unwrap the tuple data
        const tuples = DeqoData.unwrap(ddt);

        // Always inject into the model root, since generally data tuples are created relative to the model root.
        const root = objects.get('root', Root);

        objects.update(u => {
            const phys = [] as Physical[];
            for (const cmpt of u.activateAll(tuples, options.useRefAsId)) {
                root.add(cmpt);
                const p = cmpt.as(Physical);
                if (!p) continue;
                cascadedUnlock(p);
                phys.push(p);
            }
            let view = this.camera.getModelFrame();
            let bounds = Physicals.bounds(phys);
            if (!!options.position) {
                // If we have a inject position, compute and apply the offset to move the objects to the inject point
                if (options.position !== 'auto') {
                    const offset = Point.vector(bounds.center(), options.position);
                    phys.forEach(x => x.moveBy(offset));
                }
                // Otherwise, if the objects are completely out of view, move them into view
                else if (!view.contains(bounds) && !view.intersects(bounds)) {
                    const offset = Point.vector(bounds.center(), view.center());
                    phys.forEach(x => x.moveBy(offset));
                }
                // Recompute bounds as things probably moved
                bounds = Physicals.bounds(phys);
            }
            if (!!options.space) {
                const lookup = new Set(phys) as Set<Component>;
                const existing = Physicals.bounds(
                    root.children.filter(x => x.is(Physical) && !lookup.has(x)) as Physical[]
                );
                if (bounds.intersects(existing)) {
                    // Round 10% of bounds to nearest 100
                    const to = this.grid.snap(new Point(
                        existing.right + options.space,
                        existing.top + (existing.height - bounds.height) / 2,
                    ));
                    const offset = Point.vector(bounds.topLeft(), to);
                    phys.forEach(p => p.moveBy(offset));
                    // Recompute bounds as things probably moved
                    bounds = Physicals.bounds(phys);
                }
            }
            if (options.focus === 'instant') {
                // If the objects are partially in view, move to objects
                if (!view.contains(bounds)) {
                    const offset = view.encapsulationVector(bounds).inverse();
                    this.camera.move(offset.multiply(this.camera.scale));
                    view = this.camera.getModelFrame();
                }
                // If the view is too small to see everything, zoom out
                if (!view.inflate(10).contains(bounds)) {
                    this.camera.setFrame(bounds);
                }
            }
            if (options.focus === 'animate') {
                // If the objects are partially/not in view, or are tiny, animate to objects
                if (!view.inflate(10).contains(bounds)) {
                    this.camera.animateToFrame(bounds);
                }
            }
            if (options.place) {
                // dispatch(place);
            }
            session.update(Selection, SelectionSet.create(phys as any));
        });

    }

    public async injectFromClipData(clip: ClipData): Promise<boolean> {
        if (!this.canEdit()) return;

        // Priority:
        // 1. x/deqo-*
        // 2. anything but text/plain
        // 3. text/plain

        const order = clip.types().slice().sort(typeSorter);

        for (const type of order) {
            const data = clip.get(type);
            const payload = ContentPayload.create(type, data, { method: 'clipboard' });
            const success = await this.inject(payload);
            if (success) return true;
        }

        return false;
    }

    public async inject(content: ContentPayload): Promise<boolean> {
        if (!this.canEdit()) return;
        console.log(content, content?.type);
        if (content.type === 'x/deqo-tuple') {
            this.injectNative(content.data, {
                position: content.metadata.position ?? 'auto',
                focus: 'instant',
                place: true,
            });
            return true;
        }

        const compatible = Array.from(this.processors.get(content.type)).sort(processorSorter);
        for (const cp of compatible) {
            const result = await cp.process(content);
            if (result === true) return true;
            if (result === false) continue;
            return await this.inject(result);
        }

        // If failed and the content was derived, try the original but with the force option
        if (content.original && !content.metadata.force) {
            return await this.inject(ContentPayload.underive(content, { force: true }));
        }

        return false;

    }

    private canEdit() {
        return this.session.get(MyRoleInfo).canParticipate;
    }
}

export const inContentProcessors = (...cps: Type<ContentProcessor>[]): Connector =>
    r => void (cps.forEach(x => r.call(ContentInjector).extend(x)));

export const inStandardContentProcessors = inContentProcessors(
    SerializedDeqoDataProcessor, TextDataProcessor, CsvDataProcessor, JsonDataProcessor, DeqoStyleDataProcessor);
